/**
 * Shared display helpers for virtual pallets.
 * Single source of truth for:
 *   - the display brand label shown for pallet products on the storefront
 *     (the raw `models.brand` is "OpenBox", which is the seller name and
 *     should NEVER be surfaced to customers as a manufacturer brand)
 *   - whether a pallet should display the Factory Refurbished badge
 *     instead of a generic open-box "B" grade badge
 *
 * IMPORTANT: keep the underlying `models.brand` data unchanged
 * ("OpenBox") so that the AC pallet importer remains idempotent
 * (find-or-create by mfgSku still matches existing rows). All
 * customer-facing brand presentation is layered on at render time.
 */

export const FACTORY_REFURBISHED_LABEL = "Factory Refurbished";
export const PALLET_DEFAULT_DISPLAY_BRAND = "Multi-Brand Pallet";

/**
 * Sidebar grade-row label used for factory-refurbished pallets in the
 * catalog. Distinct from the cosmetic "B" grade so the AC subcategory
 * grade row reads "B — Factory Refurbished" rather than colliding with
 * the unrelated open-box B/B+/B- scale used for refrigerator returns.
 */
export const PALLET_GRADE_LABEL = "B — Factory Refurbished";

/**
 * Anchor on /grading where the AC factory-refurbished callout lives.
 * The Factory Refurbished badge tooltip/link points here so customers
 * can read the full explanation in one click.
 */
export const FACTORY_REFURBISHED_GRADING_ANCHOR = "/grading#air-conditioners";

/** Threshold above which a single brand is considered the "dominant" brand of a pallet. */
const DOMINANT_BRAND_THRESHOLD = 0.8;

export type PalletProgramType = "AC" | string;

export interface PalletDisplayInput {
  isPallet?: boolean | null;
  /** Raw seller brand on the model row (e.g., "OpenBox"). */
  brand?: string | null;
  /** Brands of the constituent SKUs that make up the pallet, in order of qty desc. */
  palletConstituentBrands?: string[] | null;
  /** Per-line quantities aligned with palletConstituentBrands (optional). */
  palletConstituentBrandQuantities?: Record<string, number> | null;
  attributes?: Record<string, any> | null;
}

/**
 * Returns the brand label that should be shown to customers for a pallet
 * product. Non-pallet models return their raw brand unchanged.
 *
 * Rules for pallets:
 *   - If one constituent brand makes up >= 80% of the units, return
 *     "<Brand> + others" (or just "<Brand>" if it's the only brand).
 *   - Otherwise return the localized "Multi-Brand Pallet" label.
 *   - If we don't have constituent data, fall back to the same label
 *     (NOT "OpenBox").
 */
export function getDisplayBrand(model: PalletDisplayInput): string {
  if (!model?.isPallet) return model?.brand || "";

  const brands = model.palletConstituentBrands || [];
  const qtys = model.palletConstituentBrandQuantities || null;

  if (brands.length === 0) {
    return PALLET_DEFAULT_DISPLAY_BRAND;
  }
  if (brands.length === 1) {
    return brands[0]!;
  }

  if (qtys) {
    const total = Object.values(qtys).reduce((sum, n) => sum + (n || 0), 0);
    if (total > 0) {
      const sorted = brands
        .slice()
        .sort((a, b) => (qtys[b] || 0) - (qtys[a] || 0));
      const top = sorted[0]!;
      const topShare = (qtys[top] || 0) / total;
      if (topShare >= DOMINANT_BRAND_THRESHOLD) {
        return `${top} + others`;
      }
    }
  }

  return PALLET_DEFAULT_DISPLAY_BRAND;
}

/**
 * Returns true when the pallet should be presented as Factory Refurbished
 * rather than as a generic "B" cosmetic-graded open-box unit.
 *
 * Phase 1: only AC pallets. Microwave / other future programs default to
 * the standard cosmetic grading scale until we explicitly opt them in.
 */
export function isFactoryRefurbished(model: PalletDisplayInput): boolean {
  if (!model?.isPallet) return false;
  const program = (model.attributes?.programType as string | undefined) || null;
  return program === "AC";
}

/**
 * For brand-aggregation in the catalog sidebar, return the list of brands
 * a product should contribute to its category's brand pill list.
 *
 * - Non-pallet products contribute their own raw brand.
 * - Pallet products contribute their constituent brands so the sidebar
 *   shows the actual manufacturers (LG / Frigidaire / Midea / Hisense)
 *   instead of the seller name "OpenBox".
 * - Pallets with no enriched constituent data are skipped (they would
 *   otherwise pollute the brand list with "OpenBox").
 */
export function getCatalogContributingBrands(model: PalletDisplayInput): string[] {
  if (model?.isPallet) {
    const brands = model.palletConstituentBrands || [];
    return Array.from(new Set(brands.filter(Boolean)));
  }
  return model?.brand ? [model.brand] : [];
}

/**
 * For brand-filter matching: should this product be shown when the user
 * has selected `selectedBrands` in its category?
 *
 * - Non-pallet products match if their own brand is in the selection.
 * - Pallet products match if ANY of their constituent brands is in the
 *   selection (so picking "LG" surfaces every pallet that contains LG units).
 */
export function matchesBrandFilter(
  model: PalletDisplayInput,
  selectedBrands: string[],
): boolean {
  if (selectedBrands.length === 0) return true;
  const lower = selectedBrands.map((b) => b.toLowerCase());
  if (model?.isPallet) {
    const brands = model.palletConstituentBrands || [];
    return brands.some((b) => b && lower.includes(b.toLowerCase()));
  }
  const brand = model?.brand?.toLowerCase() || "";
  return lower.includes(brand);
}

/**
 * For sidebar grade-row aggregation AND grade-filter matching, return the
 * grade labels a product should contribute to its category's grade pill list.
 *
 * - Factory-refurbished pallets contribute the dedicated PALLET_GRADE_LABEL
 *   ("B — Factory Refurbished") instead of mixing into the cosmetic
 *   A/B+/B- scale.
 * - Non-factory-refurbished products fall back to their cosmetic grades
 *   (whatever availableGrades was passed in).
 */
export function getCatalogGradeLabels(
  model: PalletDisplayInput,
  availableGrades: string[] | null | undefined,
): string[] {
  if (isFactoryRefurbished(model)) {
    return [PALLET_GRADE_LABEL];
  }
  return Array.isArray(availableGrades) ? availableGrades.filter((g): g is string => !!g) : [];
}
